"use client"

import { useState, useEffect, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from 'react-redux';
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { tournamentHouseFeeService } from '../apis/tournamentHouseFeeService'
import { toast } from "react-hot-toast"
import moment, { defaultDateRange, isDateTimeInRange } from "../utils/timezone"
import { TournamentHomeAddModal } from "../components/tournamenthome-add-modal"
import { TournamentHomeEditModal } from "../components/tournamenthome-edit-modal"
import { TournamentHomeDeleteModal } from "../components/tournamenthome-delete-modal"
import { TournamentHouseFeeTransactionTable } from "../components/tournament-house-fee-transaction-table"
import DateRangePicker from '../components/ui/date-range-picker'
import { Calendar, Pencil } from "lucide-react"
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from "../context/AuthContext"
import AdminEditModalTornamenthouse from "../components/AdminEditModalTornamenthouse"
import { setTournamentHouseTransactionDetails } from '../Store/actions/transactionDetails'
import { base64Logo } from '../constants/pdfLogo'

// Helper functions
const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const MIN_TOURNAMENT_HOUSE_BALANCE = 2000;


export default function TournamentHouseFees() {
    const dispatch = useDispatch();
    const { currentUser } = useAuth();
    const transactionDetails = useSelector(state => state.transactionDetails.tournamentHouseTransactionDetails);
    
    const [user, setUser] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [currentBalance, setCurrentBalance] = useState(0);
    const [minimumBalance, setMinimumBalance] = useState(MIN_TOURNAMENT_HOUSE_BALANCE);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [dateRange, setDateRange] = useState(defaultDateRange);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [currentTransaction, setCurrentTransaction] = useState(null);
    const [mostRecentId, setMostRecentId] = useState(null);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [transactionDetailsUpdated, setTransactionDetailsUpdated] = useState(false);
    const [isAdminEditModalOpen, setIsAdminEditModalOpen] = useState(false);

    // Load saved transaction details from localStorage on component mount
    useEffect(() => {
        const savedDetails = localStorage.getItem('transactionDetailstournamenthouse');
        if (savedDetails) {
            dispatch(setTournamentHouseTransactionDetails(JSON.parse(savedDetails)));
        }
    }, [dispatch]);

    // Initialize user from localStorage
    useEffect(() => {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                setUser(JSON.parse(userData));
            }
        } catch (error) {
            console.error('Error parsing user data:', error);
            setUser(null);
        }
    }, []);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await tournamentHouseFeeService.getEmployees();
                if (response.success) {
                    setEmployees(response?.data?.body?.data?.employees);
                }
            } catch (error) {
                console.error('Error fetching employees:', error);
            }
        };

        fetchEmployees();
    }, []);


    // Calculate summary based on date range
    const dateRangeSummary = useMemo(() => {
        const filteredByDateRange = transactions.filter(transaction => {
            return isDateTimeInRange(transaction.date, dateRange.start, dateRange.end);
        });

        const deposit = filteredByDateRange.reduce((sum, tx) =>
            tx.transaction_type === "deposit" ? sum + parseNumericValue(tx.transaction_amount) : sum, 0);

        const withdraw = filteredByDateRange.reduce((sum, tx) =>
            tx.transaction_type === "withdraw" ? sum + parseNumericValue(tx.transaction_amount) : sum, 0);

        // Calculate balance based on date range transactions
        const transactionsOutsideRange = transactions.filter(transaction => {
            return !isDateTimeInRange(transaction.date, dateRange.start, dateRange.end);
        });

        let balanceAdjustment = 0;
        transactionsOutsideRange.forEach(tx => {
            if (tx.transaction_type === "deposit") {
                balanceAdjustment -= parseNumericValue(tx.transaction_amount);
            } else {
                balanceAdjustment += parseNumericValue(tx.transaction_amount);
            }
        });

        const calculatedBalance = currentBalance + balanceAdjustment;

        return {
            deposit,
            withdraw,
            balance: calculatedBalance
        };
    }, [transactions, dateRange, currentBalance]);

    // Fixed fetchData function - removed unnecessary dependencies
    const fetchData = useCallback(async () => {
        if (!user) return;

        try {
            setLoading(true);

            if (user.role === "Cashier") {
                const verificationRes = await tournamentHouseFeeService.checkVerificationStatus();
                if (verificationRes.success) {
                    if (verificationRes.data.status === "unverified") {
                        setLoading(false);
                        return;
                    }
                }
            }

            const [balanceRes, transactionsRes] = await Promise.all([
                tournamentHouseFeeService.getCurrentBalance(),
                tournamentHouseFeeService.getAllTransactions()
            ]);

            if (balanceRes.success && transactionsRes.success) {
                const balance = parseNumericValue(balanceRes.data.balance);
                const minBalance = parseNumericValue(balanceRes.data.minimumBalance) || MIN_TOURNAMENT_HOUSE_BALANCE;
                const transactionData = transactionsRes.data || [];

                // Sort transactions by date descending
                const sortedTransactions = [...transactionData].sort((a, b) =>
                    new Date(b.date) - new Date(a.date)
                );

                setCurrentBalance(balance);
                setMinimumBalance(minBalance);
                setTransactions(sortedTransactions);

                // Set most recent transaction ID for deletion validation
                if (sortedTransactions.length > 0) {
                    setMostRecentId(sortedTransactions[0].id);
                }
            } else {
                toast.error("Failed to load data");
            }
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    }, [user]); // Only depend on user

    // Fixed useEffect - now includes fetchData in dependencies
    useEffect(() => {
        fetchData();
    }, [fetchData, refreshFlag]); // Include both fetchData and refreshFlag

    const openAddModal = () => setIsAddModalOpen(true);
    const openEditModal = (transaction) => {
        setCurrentTransaction(transaction);
        setIsEditModalOpen(true);
    };
    const openDeleteModal = (transaction) => {
        setCurrentTransaction(transaction);
        setIsDeleteModalOpen(true);
    };

    const recalculateBalances = (txList) => {
        const sortedTx = [...txList].sort((a, b) => new Date(a.date) - new Date(b.date));

        return sortedTx.map((tx, index) => {
            const amount = parseNumericValue(tx.transaction_amount);

            // For first transaction
            if (index === 0) {
                const beforeBalance = tx.transaction_type === "deposit" ? 0 : amount;
                const afterBalance = tx.transaction_type === "deposit" ? amount : 0;
                return {
                    ...tx,
                    before_balance: beforeBalance,
                    after_balance: afterBalance
                };
            }

            // For subsequent transactions
            const prevTx = sortedTx[index - 1];
            const beforeBalance = parseNumericValue(prevTx.after_balance);
            const afterBalance = tx.transaction_type === "deposit"
                ? beforeBalance + amount
                : beforeBalance - amount;

            return {
                ...tx,
                before_balance: beforeBalance,
                after_balance: afterBalance
            };
        });
    };

    const updateTransactionChain = (updatedTx) => {
        const recalculated = recalculateBalances(updatedTx);
        const sorted = [...recalculated].sort((a, b) =>
            new Date(b.date) - new Date(a.date)
        );

        setTransactions(sorted);

        // Update current balance to the latest transaction's after_balance
        if (sorted.length > 0) {
            setCurrentBalance(parseNumericValue(sorted[0].after_balance));
        } else {
            setCurrentBalance(0);
        }

        // Update most recent ID
        setMostRecentId(sorted.length > 0 ? sorted[0].id : null);

    };

    const handleAddTransaction = async (formData) => {
        try {
            setError(null);
            const response = await tournamentHouseFeeService.addTransaction(formData);

            if (response.success) {
                toast.success(response.message || "Transaction added successfully");
                const newTransaction = response.data;

                // Optimistic update
                const updatedTransactions = [newTransaction, ...transactions];
                updateTransactionChain(updatedTransactions);
                setRefreshFlag(prev => !prev);
                return true;
            } else {
                setError(response.error || "Transaction failed");
                toast.error(response.error || "Transaction failed");
                return false;
            }
        } catch (error) {
            console.error('Submit error:', error);
            setError("Transaction failed");
            toast.error("Transaction failed");
            return false;
        }
    };

    const handleUpdateTransaction = async (id, updatedData) => {
        try {
            const response = await tournamentHouseFeeService.updateTransaction(id, updatedData);

            if (response.success) {
                toast.success(response.message || "Transaction updated successfully");

                // Optimistic update
                const updatedTransactions = transactions.map(tx =>
                    tx.id === id ? { ...tx, ...response.data } : tx
                );
                updateTransactionChain(updatedTransactions);
                setRefreshFlag(prev => !prev);
                return true;
            } else {
                toast.error(response.error || "Update failed");
                return false;
            }
        } catch (error) {
            console.error('Edit error:', error);
            toast.error("Update failed");
            return false;
        }
    };

    const handleDeleteTransaction = async (id) => {
        try {
            const response = await tournamentHouseFeeService.deleteTransaction(id);

            if (response.success) {
                toast.success(response.message || "Transaction deleted successfully");

                // Optimistic update
                const updatedTransactions = transactions.filter(tx => tx.id !== id);
                updateTransactionChain(updatedTransactions);
                setRefreshFlag(prev => !prev);
                return true;
            } else {
                toast.error(response.error || "Deletion failed");
                return false;
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error("Deletion failed");
            return false;
        }
    };


    const handleDateRangeApply = (startDate, endDate) => {
        setDateRange({ start: startDate, end: endDate });
        setIsDatePickerOpen(false);
        setCurrentPage(1);
    };

    const formatDateRange = () => {
        return `${moment(dateRange.start).format("MM/DD/YYYY")} - ${moment(dateRange.end).format("MM/DD/YYYY")}`;
    };

    // Filter transactions based on search term and date range
    const filteredTransactions = transactions
        .filter(transaction => {
            const matchesSearch = !searchTerm ||
                (transaction.notes && transaction.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (transaction.added_by && transaction.added_by.toLowerCase().includes(searchTerm.toLowerCase()));

            const matchesDateRange = isDateTimeInRange(transaction.date, dateRange.start, dateRange.end);

            return matchesSearch && matchesDateRange;
        })
        .map(transaction => ({
            ...transaction,
            isDeletable: transaction.id === mostRecentId
        }));

    if (!user) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="p-6 bg-gray-50">
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                    </div>
                </div>
            </div>
        );
    }


    const exportPettyCashToPDF = () => {
        try {
            const doc = new jsPDF();

            // Set document properties


            const logoWidth = 40; // adjust width as needed
            const logoHeight = 20; // adjust height as needed
            doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight); // x, y, width, height


            doc.setProperties({
                title: 'Tournament House Cash Report',
                subject: 'Tournament House Fees',
                author: 'Elite Poker Lounge',
                creator: 'Tournament House Cash System'
            });

            // Add timestamp
            const now = new Date();
            const dateTime = now.toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Date & Time: ${dateTime}`, 14, 15);
            doc.text(`Date Range: ${formatDateRange()}`, 14, 25);

            // Title
            doc.setFontSize(18);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Tournament House Fees', 14, 40);

            // Summary section
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text('Summary:', 14, 50);

            doc.setFont(undefined, 'normal');
            doc.text(`Total Deposit: $${formatCurrency(dateRangeSummary.deposit)}`, 14, 60);
            doc.text(`Total Withdraw: $${formatCurrency(dateRangeSummary.withdraw)}`, 14, 70);
            doc.text(`Current Balance: $${formatCurrency(currentBalance)}`, 14, 80);

            // Tournament-wise totals (group by transaction detail / notes)
            const tournamentTotals = {};
            filteredTransactions.forEach((tx) => {
                const key = (tx.notes || '').trim() || 'Unspecified';
                if (!tournamentTotals[key]) {
                    tournamentTotals[key] = { withdraw: 0, deposit: 0 };
                }
                const amount = parseNumericValue(tx.transaction_amount);
                if (tx.transaction_type === 'deposit') {
                    tournamentTotals[key].deposit += amount;
                } else if (tx.transaction_type === 'withdraw') {
                    tournamentTotals[key].withdraw += amount;
                }
            });

            const tournamentEntries = Object.entries(tournamentTotals).sort((a, b) =>
                a[0].localeCompare(b[0])
            );

            let yPos = 92;
            doc.setFont(undefined, 'bold');
            doc.setFontSize(12);
            doc.text('Tournament Wise Transaction:', 14, yPos);
            yPos += 8;

            doc.setFont(undefined, 'normal');
            doc.setFontSize(10);

            if (tournamentEntries.length === 0) {
                doc.text('No tournament transactions in this range.', 14, yPos);
                yPos += 8;
            } else {
                tournamentEntries.forEach(([tournamentName, totals], index) => {
                    const parts = [`${index + 1}. ${tournamentName}:`];
                    if (totals.withdraw > 0) {
                        parts.push(`Total Withdraw = $${formatCurrency(totals.withdraw)}`);
                    }
                    if (totals.deposit > 0) {
                        parts.push(`Total Deposit = $${formatCurrency(totals.deposit)}`);
                    }
                    if (totals.withdraw === 0 && totals.deposit === 0) {
                        parts.push('Total Withdraw = $0.00 | Total Deposit = $0.00');
                    }
                    const line = parts.length > 2
                        ? `${parts[0]} ${parts.slice(1).join(' | ')}`
                        : `${parts[0]} ${parts[1]}`;
                    const wrapped = doc.splitTextToSize(line, 180);

                    if (yPos + wrapped.length * 5 > 280) {
                        doc.addPage();
                        yPos = 20;
                    }

                    doc.text(wrapped, 14, yPos);
                    yPos += wrapped.length * 5 + 2;
                });
            }

            yPos += 6;

            // Prepare table data
            const tableHeaders = [
                'Date',
                'Tournament House Balance',
                'Transaction',
                'Transaction Details',
                'After Balance',
                'Added By'
            ];

            const tableData = filteredTransactions.map(transaction => {
                const transactionAmount = transaction.transaction_type === 'deposit'
                    ? `+${formatCurrency(transaction.transaction_amount)}`
                    : `-${formatCurrency(transaction.transaction_amount)}`;

                return [
                    moment(transaction.date).format("MM/DD/YYYY hh:mm A"),
                    `$${formatCurrency(transaction.before_balance)}`,
                    transactionAmount,
                    transaction.notes || 'N/A',
                    `$${formatCurrency(transaction.after_balance)}`,
                    transaction.added_by || 'N/A'
                ];
            });

            // Add table
            autoTable(doc, {
                head: [tableHeaders],
                body: tableData,
                startY: yPos,
                theme: 'plain',
                styles: {
                    fontSize: 9,
                    cellPadding: 3,
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1
                },
                headStyles: {
                    fillColor: false,
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                    lineWidth: 0.1,
                    lineColor: [0, 0, 0]
                },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 20 },
                    3: { cellWidth: 40 },
                    4: { cellWidth: 25 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 30 }
                },
                margin: { left: 14, right: 14 },
                tableLineColor: [0, 0, 0],
                tableLineWidth: 0.1
            });

            // Save the PDF
            const fileName = `Tournament_House_Fees_Report_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Error generating PDF. Please try again.');
        }
    };

    const handleAdminEditSave = (updatedDetails) => {
        dispatch(setTournamentHouseTransactionDetails(updatedDetails));
        localStorage.setItem('transactionDetailstournamenthouse', JSON.stringify(updatedDetails));
        setTransactionDetailsUpdated(prev => !prev);
    };


    // Update the Admin/edit button click handler
    const handleAdminEditClick = () => {
        setIsAdminEditModalOpen(true);
    };

    return (
        <div className="bg-gray-50 p-6">
            <div className="w-full mx-auto">


                <div className="flex justify-between items-center w-full mb-8">
                    <div className="text-center flex-1">
                        <h1 className="text-3xl font-bold text-gray-800 tracking-tight">
                            Tournament House Fees
                        </h1>
                        <p className="text-gray-600 mt-2 text-sm">
                            Manage and track tournament house fees
                        </p>
                    </div>
                    {currentUser?.role?.trim() === "Admin" && (
                        <div
                            className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 px-3 py-1.5 rounded-md border border-gray-300 hover:border-blue-500 shadow-sm transition-colors duration-200 cursor-pointer"
                            onClick={handleAdminEditClick}
                        >
                            <Pencil className="w-4 h-4" />
                            <span className="font-medium">Admin</span>
                        </div>
                    )}


                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <Card className="bg-white shadow-sm border border-gray-200">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                                Total Deposit
                                <span className="block text-xs text-gray-400 normal-case mt-1">
                                    ({formatDateRange()})
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-600">
                                    ${formatCurrency(dateRangeSummary.deposit)}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="bg-white shadow-sm border border-gray-200">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                                Total Withdraw
                                <span className="block text-xs text-gray-400 normal-case mt-1">
                                    ({formatDateRange()})
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-red-600">
                                    ${formatCurrency(dateRangeSummary.withdraw)}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className={`bg-white shadow-sm border ${currentBalance < minimumBalance ? 'border-red-300' : 'border-gray-200'}`}>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wide text-center">
                                Current Balance
                                <span className="block text-xs text-gray-400 normal-case mt-1">
                                    (Live Balance · Min ${formatCurrency(minimumBalance)})
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                            <div className="text-center">
                                <div className={`text-2xl font-bold ${currentBalance < minimumBalance ? 'text-red-600' : 'text-blue-600'}`}>
                                    ${formatCurrency(currentBalance)}
                                </div>
                                {currentBalance < minimumBalance && (
                                    <p className="text-xs text-red-600 mt-2 font-medium">
                                        Below required minimum of ${formatCurrency(minimumBalance)}
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Transaction Form */}
                <Card className="bg-white shadow-sm border border-gray-200 mb-8">
                    <CardHeader>
                        <CardTitle className="text-lg font-semibold text-gray-800">
                            Add New Transaction
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex justify-center">
                            <Button
                                onClick={openAddModal}
                                className="h-12 px-8 bg-yellow-400 hover:bg-yellow-500 text-black font-medium"
                            >
                                Add New Transaction
                            </Button>
                        </div>
                        {error && (
                            <div className="mt-4 text-center text-red-600 font-medium">
                                {error}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Transaction History */}
                <Card className="bg-white shadow-sm border border-gray-200">
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <CardTitle className="text-lg font-semibold text-gray-800">
                                Transaction History
                            </CardTitle>
                            <Button
                                onClick={exportPettyCashToPDF}
                                variant="outline"
                                className="bg-yellow-400 hover:bg-yellow-500 text-black"
                            >
                                Export to PDF
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {/* Controls */}
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                    Date Range:
                                </label>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsDatePickerOpen(true)}
                                        className="w-64 justify-start text-left font-normal"
                                    >
                                        <Calendar className="mr-2 h-4 w-4" />
                                        {formatDateRange()}
                                    </Button>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                    Search:
                                </label>
                                <Input
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search transactions..."
                                    className="w-64"
                                />
                            </div>
                        </div>

                        {/* Loading State */}
                        {loading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="p-6 bg-gray-50">
                                    <div className="flex justify-center items-center h-64">
                                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <TournamentHouseFeeTransactionTable
                                transactions={filteredTransactions}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}
                                setEntriesPerPage={setEntriesPerPage}
                                onEdit={openEditModal}
                                onDelete={openDeleteModal}
                                dateRange={dateRange}
                                employeeApiService={tournamentHouseFeeService}
                                employees={employees}
                            />
                        )}
                    </CardContent>
                </Card>



                {/* Add Modal */}
                <TournamentHomeAddModal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onAddTransaction={handleAddTransaction}
                    currentBalance={currentBalance}
                    transactionDetails={transactionDetails}
                    key={transactionDetailsUpdated}
                />


                <AdminEditModalTornamenthouse
                    isOpen={isAdminEditModalOpen}
                    onClose={() => setIsAdminEditModalOpen(false)}
                    onSave={handleAdminEditSave}
                />
                {/* Edit Modal */}
                <TournamentHomeEditModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    transaction={currentTransaction}
                    onUpdateTransaction={handleUpdateTransaction}
                    currentUser={user}
                />

                {/* Delete Modal */}
                <TournamentHomeDeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    transaction={currentTransaction}
                    onDeleteTransaction={handleDeleteTransaction}
                />
            </div>

            {/* Date Range Picker */}
            {isDatePickerOpen && (
                <DateRangePicker
                    isOpen={isDatePickerOpen}
                    onClose={() => setIsDatePickerOpen(false)}
                    onApply={handleDateRangeApply}
                    initialStartDate={dateRange.start}
                    initialEndDate={dateRange.end}
                />
            )}
        </div>
    );
}